import { useEffect, useState } from "react"
import { Link } from "@inertiajs/react"
import LanguageSwitcher from "@/components/chalky_booking/LanguageSwitcher"
import { cn } from "@/lib/utils"

interface NavbarProps {
  brand: { line1: string; line2: string }
  nav: { services: string; agency: string; contact: string }
  locales: string[]
  currentLocale: string
  languageLabels: Record<string, string>
}

const SECTIONS = [
  { anchor: "services", labelKey: "services" as const },
  { anchor: "about", labelKey: "agency" as const },
]

export default function Navbar({
  brand,
  nav,
  locales,
  currentLocale,
  languageLabels,
}: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileOpen])

  const isHome =
    typeof window !== "undefined" && window.location.pathname === "/"

  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    anchor: string
  ) => {
    if (!isHome) {
      return
    }
    e.preventDefault()
    const target = document.getElementById(anchor)
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" })
    }
    setMobileOpen(false)
  }

  const Brand = () => (
    <>
      <span>{brand.line1}</span>
      <span className="font-light ml-1">{brand.line2}</span>
    </>
  )

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-light border-b border-contour">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center h-20">
            <Link
              href="/"
              className="text-2xl font-bold text-contrast tracking-tight"
            >
              <Brand />
            </Link>

            <div className="hidden md:flex items-center gap-8">
              {SECTIONS.map(({ anchor, labelKey }) => (
                <a
                  key={anchor}
                  href={isHome ? `#${anchor}` : `/#${anchor}`}
                  onClick={(e) => handleAnchorClick(e, anchor)}
                  className="text-content hover:text-contrast transition-colors font-medium tracking-wide"
                >
                  {nav[labelKey]}
                </a>
              ))}
              <a
                href={isHome ? "#contact" : "/#contact"}
                onClick={(e) => handleAnchorClick(e, "contact")}
                className="px-6 py-2 bg-contrast text-light font-medium tracking-wide hover:bg-secondary transition-all duration-300"
              >
                {nav.contact}
              </a>

              <div className="ml-4">
                <LanguageSwitcher
                  locales={locales}
                  current={currentLocale}
                  labels={languageLabels}
                />
              </div>
            </div>

            <div className="md:hidden flex items-center gap-4">
              <LanguageSwitcher
                locales={locales}
                current={currentLocale}
                labels={languageLabels}
                variant="mobile"
              />

              <button
                type="button"
                aria-label="Menu"
                className="text-contrast"
                onClick={() => setMobileOpen(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div
        className={cn(
          "fixed inset-0 bg-contrast bg-opacity-50 z-40 md:hidden transition-opacity duration-300",
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMobileOpen(false)}
      />

      <div
        className={cn(
          "fixed top-0 right-0 h-full w-80 bg-light shadow-2xl z-50 transform transition-transform duration-300 md:hidden",
          mobileOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-12">
            <span className="text-xl font-bold text-contrast tracking-tight">
              <Brand />
            </span>
            <button
              type="button"
              aria-label="Fermer le menu"
              className="text-contrast"
              onClick={() => setMobileOpen(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {SECTIONS.map(({ anchor, labelKey }) => (
              <a
                key={anchor}
                href={isHome ? `#${anchor}` : `/#${anchor}`}
                onClick={(e) => handleAnchorClick(e, anchor)}
                className="block text-lg text-content hover:text-contrast transition-colors font-medium"
              >
                {nav[labelKey]}
              </a>
            ))}

            <div className="pt-6 border-t border-contour">
              <a
                href={isHome ? "#contact" : "/#contact"}
                onClick={(e) => handleAnchorClick(e, "contact")}
                className="block w-full py-3 bg-contrast text-light text-center font-medium tracking-wide hover:bg-secondary transition-all duration-300"
              >
                {nav.contact}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
